import { Injectable } from '@angular/core';
import {
  Router,
  RouterStateSnapshot,
  ActivatedRouteSnapshot,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@uw-env/environment';
import { CustomNavigationService } from '@uw/core/custom-navigation.service';
import { PostLoginDestinationPage } from '@uw/core/enums/post-login-destination-page.enum';
import { CampaignInfo } from '@uw/core/models/campaign.model';

import { AuthService } from '@uw/core/services/auth.service';
import { CampaignService } from '@uw/core/services/campaign.service';

import { Observable, Subject, combineLatest, of, map, mergeMap, switchMap, tap, lastValueFrom } from 'rxjs';

@Injectable()
export class AuthPagesResolver {
  constructor(
    protected router: Router,
    protected authService: AuthService,
    protected campaignService: CampaignService,
    private customNavigationService: CustomNavigationService,
    private translateService: TranslateService
  ) {}

  resolve(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<unknown> | Promise<null> {
    let obs$;

    const token = localStorage.getItem('token');

    // check for the lang queryparm first. This is helpful if logout called from admin site to route the user to lang specific url.
    if (route.queryParams && route.queryParams.lang) {
      localStorage.setItem('lang', ['en', 'fr'].includes(route.queryParams.lang) ? route.queryParams.lang: 'en');
      this.translateService.use(route.queryParams.lang);
      this.translateService.currentLang = route.queryParams.lang;
    }

    // Scenario to cover switching to donation site from admin site
    if (route.queryParams && route.queryParams.access) {
      // store the access token received as query param in localstorage
      // in order to provide value for authorization header for refresh-token api


      obs$ = this.authService.refreshToken(null).pipe(
        tap(( token: any) => localStorage.setItem('token', token as string)),
        mergeMap((_) => this.authService.fetchProfile()),
        switchMap(_ => combineLatest([this.campaignService.getDonorActiveCampaignList(), this.campaignService.getLatestClosedCampaignGuid()])),
        map(this._redirectUserOnDonationSite())
      );
    } else if (route.queryParams && route.queryParams.ssoAccess) {
      // This is the scenario where the sso login navigates the user to this site and based on the role of this user he will get redirected to appropriate page.

      // store the access token received as query param in localstorage
      // in order to provide value for authorization header for refresh-token api

      obs$ = this.authService.refreshToken(null).pipe(
        switchMap(( token: any) => {
          localStorage.setItem('token', token as string);
          return this.findNextNavigation(token as string);
        })
      );
    } else if (
      route.queryParams &&
      (route.queryParams.refreshTokenFailed ||
        route.queryParams.loggedout ||
        route.queryParams.authorizationFailed)
    ) {

      const isSSOUser = route.queryParams.isSSOUser;

      // Clear the local storage if refresh token api failed or user has been logged out.
      // These query params are set from admin site,
      // when the user gets navigated to the login page which is implemented on the donor site.

      const intentionalLogout = !!route.queryParams.loggedout;
      this.authService.clearStorage(isSSOUser, intentionalLogout);

      if (isSSOUser && isSSOUser == 'true') {
        this.customNavigationService.navigate(['/main', 'sso-login']);
      } else {
        this.customNavigationService.navigate(['/main', 'login']);
      }
    } else if (token) {
      // This is to address a scenario where an admin user logs in to the site and tries to access the base url,
      // again in a different tab.
      obs$ = this.findNextNavigation(token);
    }

    if (obs$) {
      return lastValueFrom(obs$)
        .then(
          (routeConfig: any): null => {
            if (!routeConfig) {
              return null;
            }

            this.customNavigationService.navigate(routeConfig.commands, {
              state: routeConfig.state,
            });
            return null;
          }
        )
        .catch((error: any): null => {
          const isSSOUser = localStorage.getItem('isSSOUser');

          this.authService.clearStorage(!!isSSOUser);

          if (isSSOUser && isSSOUser == 'true') {
            this.customNavigationService.navigate(['/main', 'sso-login']);
          } else {
            this.customNavigationService.navigate(['/main', 'login']);
          }

          return null;
        });
    } else {
      return of(null);
    }
  }

  private _redirectUserOnDonationSite(): (value: [CampaignInfo[], string], index: number) => { commands: string[]; } {
    return ([campaignList, latestClosedCampaignGuid]) => {
      if (campaignList && campaignList.length > 0) {
        return { commands: ['/main/campaign/select-campaign'] };
      } else if (latestClosedCampaignGuid) { // redirect the user to latest closed campaign page in case there are no active campaigns
        return { commands: ['/donor', latestClosedCampaignGuid, 'closed-campaign'] };
      } else {
        return { commands: ['/donor-info'] };
      }
    };
  }

  findNextNavigation(token: string) {
    // If token exist, navigate the user to the site based on his role.
    // This resolver will direct the admin user to admin site, or donor user to donation site.
    // If the user has both roles, then he gets directed to app selection page.
    const roles = JSON.parse(
      atob(localStorage.getItem('token').split('.')[1])
    ).roles;

    const destinationPage = this.authService.getPostLoginDestinationPage({
      roles: Array.isArray(roles) ? roles: [roles],
    });

    if (destinationPage == PostLoginDestinationPage.APP_SELECTION_PAGE) {
      // proceed to app-selection page if the logged in user has both donor role and admin roles

      return this.authService
        .fetchProfile()
        .pipe(map((_) => ({ commands: ['app-selection'], state: { token } })));
    } else if (destinationPage == PostLoginDestinationPage.DONATION_SITE) {
      // proceed to donation site if the logged in user has only donor role
      return this.authService.fetchProfile().pipe(
        switchMap(_ => combineLatest([this.campaignService.getDonorActiveCampaignList(), this.campaignService.getLatestClosedCampaignGuid()])),
        map(this._redirectUserOnDonationSite())
      );
    } else if (destinationPage == PostLoginDestinationPage.ADMIN_SITE) {
      // proceed to admin site if the logged in user has only admin role

      const sub = new Subject();

      const adminUrl = environment.adminSiteURL + '?access=switch&lang=' + localStorage.getItem('lang');
      window.location.href = adminUrl;
      setTimeout(() => {
        sub.next(null);
      }, 0);

      return sub;
    } else {
      return of(null);
    }
  }
}
